import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import { ShieldCheck, ShieldAlert, Search, Loader2, Calendar, BadgeCheck, User, Mail, FileText } from "lucide-react";

export default function VerifyCertificate() {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState(params.get("id") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (params.get("id")) verify(params.get("id"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (q) => {
    if (!q) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(`/public/verify-certificate?id=${encodeURIComponent(q)}`);
      setResult(data);
    } catch {
      setResult({ valid: false, message: "Could not verify. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setParams({ id: code });
    verify(code);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNavbar />

      <section className="bg-gradient-to-b from-blue-50/60 to-slate-50 py-16 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-brand text-xs font-black uppercase tracking-[0.2em]">
            Public Verification
          </div>
          <h1 className="heading mt-5 text-4xl sm:text-5xl font-black">Verify Certificate</h1>
          <p className="mt-3 text-slate-600">Confirm the authenticity of any PMR Welfare Foundation certificate by entering its number below.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <label className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Certificate Number</label>
          <div className="mt-2 flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. PMR/CERT/2026/00042"
              className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-brand outline-none font-mono text-sm"
              data-testid="verify-input"
            />
            <button
              type="submit"
              disabled={loading || !code}
              className="px-6 py-3 rounded-lg bg-brand hover:bg-[#172b66] text-white font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="verify-submit"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Verify
            </button>
          </div>
        </form>

        {result && (
          <div className="mt-6">
            {result.valid ? (
              <div className="bg-white rounded-2xl border-2 border-emerald-300 p-6 shadow-sm" data-testid="verify-result-valid">
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck size={24} className="text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-emerald-700 font-black text-lg flex items-center gap-1.5">
                      <BadgeCheck size={16} /> Verified Authentic
                    </div>
                    <div className="text-xs text-slate-500">This is a genuine certificate issued by {result.issued_by}.</div>
                  </div>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  <Field icon={User} label="Recipient" value={result.name} />
                  <Field icon={Mail} label="Email" value={result.email_masked} />
                  <Field icon={FileText} label="Internship" value={result.internship_title} />
                  <Field icon={Calendar} label="Completion Date" value={result.completion_date ? new Date(result.completion_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—"} />
                </div>
                <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
                  <div>Certificate No: <span className="font-mono font-bold text-slate-700">{result.certificate_no}</span></div>
                  <div>Duration: <span className="font-bold text-slate-700">{result.duration_days} days</span></div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border-2 border-red-300 p-6 shadow-sm" data-testid="verify-result-invalid">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <ShieldAlert size={24} className="text-red-600" />
                  </div>
                  <div>
                    <div className="text-red-700 font-black text-lg">Not Verified</div>
                    <div className="text-xs text-slate-500">{result.message || "Certificate not found."}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 p-4 rounded-xl bg-blue-50 border border-blue-200 text-xs text-slate-600">
          <b className="text-brand">How it works:</b> Every PMR Welfare Foundation certificate carries a unique number (e.g. <span className="font-mono">PMR/CERT/2026/00042</span>) and a QR code. Anyone can verify it here at any time — no login required.
        </div>
      </section>

      <PublicFooter contact={null} />
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1.5">
        <Icon size={11} /> {label}
      </div>
      <div className="mt-1 font-bold text-slate-900 break-words">{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}
